import { Link } from "react-router-dom";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Reveal } from "@/components/Reveal";
import { PurchaseSteps } from "@/components/PurchaseSteps";

const HERO = "/assets/doors/door-hero.jpg";
const GALLERY = [
  ...Array.from({ length: 10 }, (_, i) => `/assets/doors/studio-${i + 1}.jpg`),
  "/assets/doors/concept-2.jpg",
  "/assets/doors/concept-3.jpg",
];

const SPECS = [
  { label: "Material", value: "Solid American Oak" },
  { label: "Construction", value: "Engineered core, oak faced" },
  { label: "Finish", value: "Made to order" },
  { label: "Hardware", value: "Concealed hinges · matt black" },
  { label: "Application", value: "Interior" },
  { label: "Lead Time", value: "Confirmed on order" },
];

export default function SolidDoors() {
  return (
    <div data-testid="solid-doors" className="bg-white">
      <Breadcrumbs
        items={[
          { label: "Our Collection", to: "/collection" },
          { label: "Timber Collection", to: "/collections/timber" },
          { label: "American Oak", to: "/collections/timber/american-oak" },
          { label: "Solid Doors" },
        ]}
      />

      {/* Product hero */}
      <section className="edge pt-8 pb-16 md:pt-12" data-testid="solid-doors-head">
        <div className="grid grid-cols-1 gap-10 md:grid-cols-12">
          <div className="md:col-span-6">
            <div className="group overflow-hidden bg-secondary/40">
              <img
                src={HERO}
                alt="Solid oak door"
                data-testid="solid-doors-hero-image"
                className="aspect-[3/4] w-full cursor-zoom-in object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <span className="eyebrow">American Oak / Solid Doors</span>
            <h1 className="mt-5 text-4xl font-light leading-[1.02] tracking-editorial md:text-6xl">
              Solid Oak Door
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
              A made-to-order solid American Oak door, crafted to complement our panelling and
              flooring. Built for weight, warmth and a premium, understated finish — tailored to
              your dimensions, grain direction and hardware.
            </p>

            <dl className="mt-10 space-y-1">
              {SPECS.map((s) => (
                <div key={s.label} className="flex justify-between border-b border-border/60 py-3">
                  <dt className="text-[0.72rem] uppercase tracking-[0.16em] text-muted-foreground">{s.label}</dt>
                  <dd className="text-sm tracking-editorial">{s.value}</dd>
                </div>
              ))}
            </dl>

            <Link
              to="/contact"
              data-testid="solid-doors-enquire-cta"
              className="mt-10 inline-block bg-foreground px-8 py-4 text-xs uppercase tracking-[0.18em] text-background transition-opacity hover:opacity-80"
            >
              Make an Enquiry →
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="edge pb-24 md:pb-32" data-testid="solid-doors-gallery">
        <div className="mb-10 flex items-baseline justify-between hairline pb-4">
          <h2 className="text-2xl font-light tracking-editorial md:text-3xl">Gallery</h2>
          <span className="eyebrow">Solid Oak Door</span>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-12 sm:grid-cols-3 lg:grid-cols-4">
          {GALLERY.map((src, i) => (
            <Reveal key={src} delay={(i % 4) * 0.05}>
              <div className="group overflow-hidden bg-secondary/40" data-testid={`door-gallery-${i + 1}`}>
                <img
                  src={src}
                  alt={`Solid oak door ${i + 1}`}
                  className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                  loading="lazy"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <PurchaseSteps />
    </div>
  );
}
